import { IsDateString, IsIn, IsOptional, IsString, IsUUID, MaxLength } from 'class-validator';
import type { FileStage, FileStatus } from './files.dto';

/**
 * The Clerk (municipal administrator) dashboard, and the two approvals on a
 * file the Clerk is asked for that are not a signature:
 *
 *  - **Form 4 name approval** — the Clerk approves their name being printed on
 *    a file's Form 4 (Notice to Comply) before it is signed;
 *  - **council authorization** — the Clerk records that council authorized,
 *    or declined, remedial work (a site clean-up or a demolition).
 *
 * The third approval, the contract officer's appointment, lives on the
 * appointments screen; the dashboard lists appointments and what is waiting.
 *
 * Shared by the Government Portal web app and gov-api.
 */

export const CLERK_NOTE_MAX = 2000;
export const RESOLUTION_REF_MAX = 120;

// ── Form 4 name approval ────────────────────────────────────────────────────

export const FORM4_NAME_APPROVAL_STATUSES = [
  'pending',
  'approved',
  'declined',
  'withdrawn',
] as const;
export type Form4NameApprovalStatus = (typeof FORM4_NAME_APPROVAL_STATUSES)[number];

export const FORM4_NAME_DECISIONS = ['approve', 'decline'] as const;
export type Form4NameDecision = (typeof FORM4_NAME_DECISIONS)[number];

export interface Form4NameApprovalView {
  id: string;
  fileId: string;
  fileReference: string;
  fileAddress: string;
  localGovernmentId: string;
  formId: string | null;
  status: Form4NameApprovalStatus;
  requestedById: string | null;
  requestedByName: string;
  requestNote: string | null;
  requestedAt: string;
  decidedByName: string | null;
  decidedByTitle: string | null;
  decisionComment: string | null;
  decidedAt: string | null;
}

/** `GET /files/:fileId/form4-name-approvals`. Newest first. */
export interface FileForm4NameApprovals {
  items: Form4NameApprovalView[];
  /** The newest answered-or-open request decides; null when never asked. */
  current: Form4NameApprovalStatus | null;
  /** May the caller ask the Clerk (holds `document:draft` here, none pending)? */
  canRequest: boolean;
  /** May the caller answer (holds `form4:approve` at the file's municipality)? */
  canDecide: boolean;
}

/** `POST /files/:fileId/form4-name-approvals`. */
export class RequestForm4NameApprovalDto {
  @IsOptional()
  @IsString()
  @MaxLength(CLERK_NOTE_MAX)
  note?: string;

  /** The Form 4 version the request is about, when one is already drafted. */
  @IsOptional()
  @IsUUID()
  formId?: string;
}

/** `POST /files/:fileId/form4-name-approvals/:id/decision`. Declining needs a comment. */
export class DecideForm4NameApprovalDto {
  @IsIn(FORM4_NAME_DECISIONS)
  decision!: Form4NameDecision;

  @IsOptional()
  @IsString()
  @MaxLength(CLERK_NOTE_MAX)
  comment?: string;
}

// ── Council authorization ───────────────────────────────────────────────────

export const COUNCIL_AUTHORIZATION_KINDS = ['clean_up', 'demolition'] as const;
export type CouncilAuthorizationKind = (typeof COUNCIL_AUTHORIZATION_KINDS)[number];

export const COUNCIL_AUTHORIZATION_STATUSES = [
  'pending',
  'authorized',
  'declined',
  'withdrawn',
] as const;
export type CouncilAuthorizationStatus = (typeof COUNCIL_AUTHORIZATION_STATUSES)[number];

export const COUNCIL_DECISIONS = ['authorize', 'decline'] as const;
export type CouncilDecisionInput = (typeof COUNCIL_DECISIONS)[number];

export interface CouncilAuthorizationView {
  id: string;
  fileId: string;
  fileReference: string;
  fileAddress: string;
  localGovernmentId: string;
  kind: CouncilAuthorizationKind;
  status: CouncilAuthorizationStatus;
  requestedById: string | null;
  requestedByName: string;
  requestedAt: string;
  requestNote: string | null;
  councilMeetingDate: string | null;
  resolutionRef: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  notes: string | null;
}

/** `GET /files/:fileId/council-authorizations`. Newest first. */
export interface FileCouncilAuthorizations {
  items: CouncilAuthorizationView[];
  /** True when remedial work may be recorded: an authorization stands, or an emergency bypass. */
  remedialWorkAuthorized: boolean;
  /** An s. 144(9) emergency bypass is in force, which needs no council authorization. */
  emergencyBypass: boolean;
  /** May the caller ask (holds `file:update` on the file)? */
  canRequest: boolean;
  /** May the caller record council's decision (holds `council_authorization:record`)? */
  canDecide: boolean;
}

/** `POST /files/:fileId/council-authorizations`. */
export class RequestCouncilAuthorizationDto {
  @IsIn(COUNCIL_AUTHORIZATION_KINDS)
  kind!: CouncilAuthorizationKind;

  @IsOptional()
  @IsString()
  @MaxLength(CLERK_NOTE_MAX)
  note?: string;
}

/**
 * `POST /files/:fileId/council-authorizations/:id/decision`.
 *
 * Authorizing needs the meeting date and the resolution reference — council
 * binds the municipality only by resolution, and the record has to say which.
 * Declining needs at least the meeting date or a note.
 */
export class DecideCouncilAuthorizationDto {
  @IsIn(COUNCIL_DECISIONS)
  decision!: CouncilDecisionInput;

  @IsOptional()
  @IsDateString()
  councilMeetingDate?: string;

  @IsOptional()
  @IsString()
  @MaxLength(RESOLUTION_REF_MAX)
  resolutionRef?: string;

  @IsOptional()
  @IsString()
  @MaxLength(CLERK_NOTE_MAX)
  notes?: string;
}

/**
 * What, if anything, is missing from a council decision. Shared so the dialog
 * and the API refuse the same thing for the same reason.
 */
export function councilDecisionProblem(
  dto: Pick<
    DecideCouncilAuthorizationDto,
    'decision' | 'councilMeetingDate' | 'resolutionRef' | 'notes'
  >,
): 'meeting_date' | 'resolution_ref' | 'reason' | null {
  if (dto.decision === 'authorize') {
    if (!dto.councilMeetingDate?.trim()) return 'meeting_date';
    if (!dto.resolutionRef?.trim()) return 'resolution_ref';
    return null;
  }
  if (!dto.councilMeetingDate?.trim() && !dto.notes?.trim()) return 'reason';
  return null;
}

// ── The dashboard ───────────────────────────────────────────────────────────

/** One officer appointment, as the Clerk reads it. */
export interface ClerkDashboardAppointment {
  id: string;
  officerName: string;
  officerEmail: string;
  /** The employer the appointment runs through; null for the municipality's own employee. */
  companyName: string | null;
  appointedOn: string | null;
  expiresOn: string | null;
  status: 'pending_council' | 'active' | 'expired' | 'revoked' | 'rejected';
  badgeNumber: string;
}

/** One active file and where it stands. */
export interface ClerkDashboardFile {
  id: string;
  reference: string;
  address: string;
  stage: FileStage;
  /** At `escalation`, the furthest Step 5–9 with anything recorded; else null. */
  escalationStep: 'step_5' | 'step_6' | 'step_7' | 'step_8' | 'step_9' | null;
  status: FileStatus;
  leadOfficerName: string | null;
  openedAt: string;
  updatedAt: string;
}

/** `GET /clerk-dashboard/:localGovernmentId`. */
export interface ClerkDashboard {
  localGovernmentId: string;
  localGovernmentName: string;
  /** The municipality's licence: `full`, `limited` or `non_subscriber`. */
  accessTier: string;
  /** Files may be read but not changed here (a non-subscribing municipality). */
  readOnly: boolean;
  /** May the caller add Deputy and Assistant Clerks (holds `member:appoint`)? */
  canAddClerks: boolean;
  appointments: {
    items: ClerkDashboardAppointment[];
    /**
     * Proposals waiting on the municipality. Approving one is done on the
     * appointments screen; the dashboard links there.
     */
    waitingCount: number;
  };
  form4: {
    waiting: Form4NameApprovalView[];
    approved: Form4NameApprovalView[];
    declined: Form4NameApprovalView[];
  };
  council: {
    waiting: CouncilAuthorizationView[];
    authorized: CouncilAuthorizationView[];
    declined: CouncilAuthorizationView[];
  };
  activeFiles: {
    count: number;
    byStage: Partial<Record<FileStage, number>>;
    items: ClerkDashboardFile[];
    /** True when more files are active than `items` lists. */
    truncated: boolean;
  };
}

const ESCALATION_STEP_KEYS = ['step_9', 'step_8', 'step_7', 'step_6', 'step_5'] as const;

/**
 * The furthest escalation step a file has anything recorded at — what "the
 * stage it is at" means once the file-level stage has stopped at `escalation`.
 */
export function furthestEscalationStep(
  stageData: Record<string, unknown> | null | undefined,
): ClerkDashboardFile['escalationStep'] {
  if (!stageData) return null;
  for (const key of ESCALATION_STEP_KEYS) {
    const slice = stageData[key];
    if (slice && typeof slice === 'object' && Object.keys(slice).length > 0) return key;
  }
  return null;
}
